<script setup lang="ts">
/**
 * A picture or a chart.
 *
 * The source is a data URL, so a report carries its own images. That makes the
 * document larger and makes it self-contained, which matters more: a report
 * exported to PDF or opened next year should not depend on a file server
 * somebody has since tidied up.
 */
import { useI18n } from 'vue-i18n'

import type { ImageSection } from '@/domain/section'

const { t } = useI18n()

defineProps<{ section: ImageSection }>()
</script>

<template>
  <figure class="image" :style="{ width: `${section.width}%` }">
    <img v-if="section.source" :src="section.source" :alt="section.title" />
    <div v-else class="image__missing">{{ t('canvas.noImage') }}</div>
    <figcaption v-if="section.caption" class="page-caption">{{ section.caption }}</figcaption>
  </figure>
</template>

<style scoped>
.image {
  margin: 1em 0 1.2em;
  max-width: 100%;
}

.image img {
  display: block;
  width: 100%;
  height: auto;
}

.image__missing {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 30mm;
  border: 1px dashed var(--paper-rule);
  color: var(--paper-ink-soft);
  font-family: var(--font-ui);
  font-size: 9pt;
}
</style>
